import React from "react";
import { Link } from "react-router-dom";
import {
  FiCalendar,
  FiUser,
  FiMessageSquare,
  FiArrowLeft,
  FiChevronRight,
} from "react-icons/fi";
import { motion } from "framer-motion";
import { blogsData } from "./Blogs";
import "./BlogDetails.css";

export default function BlogDetails({ blogId }) {
  const idNum = parseInt(blogId, 10);
  const blog = blogsData.find((b) => b.id === idNum);

  if (!blog) {
    return (
      <div className="bd-page-wrapper">
        <div className="container text-center" style={{ paddingTop: "10rem" }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="bd-not-found-icon">📄</div>
            <h2 className="bd-not-found-title">Post Not Found</h2>
            <p className="bd-not-found-sub">The blog post you're looking for doesn't exist or has been removed.</p>
            <Link to="/blogs" className="bd-back-pill">
              <FiArrowLeft /> Back to Blogs
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  const relatedBlogs = blogsData.filter((b) => b.id !== idNum).slice(0, 3);

  const getAuthorBio = (role) => {
    const bios = {
      "Fullstack Developer": "Specialist in building robust web infrastructures, API integrations, and modern React interfaces.",
      "Frontend Developer": "Passionate about creating beautiful, responsive user interfaces and fluid micro-animations.",
      "Backend Developer": "Dedicated to building high-performance, secure server-side logic and scalable database schemas.",
      "UI/UX Designer": "Focused on human-centered UI systems, design thinking, and clean corporate digital layouts.",
    };
    return bios[role] || "A passionate member of The Innovative Solutions team.";
  };

  /* ─────────── Framer Motion variants ─────────── */
  const heroVariants = {
    hidden: { opacity: 0, scale: 1.04 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
  };

  const articleVariants = {
    hidden: { opacity: 0, y: 36 },
    visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 70, damping: 16, delay: 0.2 } },
  };

  const sidebarVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: { opacity: 1, x: 0, transition: { type: "spring", stiffness: 70, damping: 16, delay: 0.35 } },
  };

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 80, damping: 15 } },
  };

  return (
    <div className="bd-page-wrapper">
      {/* ── Ambient background orbs ── */}
      <div className="bd-bg-layer" aria-hidden="true">
        <div className="bd-orb bd-orb-1" />
        <div className="bd-orb bd-orb-2" />
        <div className="bd-orb bd-orb-3" />
      </div>

      {/* ── HERO BANNER ── */}
      <motion.div
        className="bd-hero"
        variants={heroVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Glow orbs & tech pattern backgrounds */}
        <div className="bd-hero-glow bd-hero-glow-1" />
        <div className="bd-hero-glow bd-hero-glow-2" />
        <div className="bd-hero-grid-pattern" />
        
        <div className="bd-hero-overlay" />

        {/* Back pill inside hero */}
        <div className="bd-hero-top-bar">
          <Link to="/blogs" className="bd-back-pill">
            <FiArrowLeft />
            <span>All Posts</span>
          </Link>
          <span className="bd-hero-category-pill">{blog.category}</span>
        </div>

        {/* Hero title block */}
        <div className="bd-hero-content">
          <motion.h1
            className="bd-hero-title"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            {blog.title}
          </motion.h1>

          <motion.div
            className="bd-hero-meta"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
          >
            <div className="bd-hero-meta-item">
              <FiCalendar />
              <span>{blog.date}</span>
            </div>
            <div className="bd-hero-meta-sep" />
            <div className="bd-hero-meta-item">
              <FiUser />
              <span>By {blog.author}</span>
            </div>
            <div className="bd-hero-meta-sep" />
            <div className="bd-hero-meta-item">
              <FiMessageSquare />
              <span>{blog.comments} Comments</span>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* ── BODY ── */}
      <div className="bd-body-container">
        <div className="row g-5 align-items-start">

          {/* ── LEFT: SIDEBAR (order-lg-1) ── */}
          <motion.div
            className="col-lg-4 order-lg-1"
            variants={sidebarVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="bd-sidebar">

              {/* Author Card */}
              <motion.div className="bd-sidebar-card bd-author-card" variants={itemVariants}>
                <div className="bd-sidebar-card-bar" />
                <div className="bd-author-avatar-ring">
                  <img
                    src={blog.image}
                    alt={blog.author}
                    className="bd-author-avatar"
                    onError={(e) => { e.target.src = "https://via.placeholder.com/100/243e7c/fff?text=A"; }}
                  />
                  <span className="bd-author-online-dot" title="Active" />
                </div>
                <h5 className="bd-author-name">{blog.author}</h5>
                <span className="bd-author-role-badge">{blog.role}</span>
                <p className="bd-author-bio">{getAuthorBio(blog.role)}</p>
              </motion.div>


              {/* Related Posts */}
              {relatedBlogs.length > 0 && (
                <motion.div className="bd-sidebar-card" variants={itemVariants}>
                  <div className="bd-sidebar-card-bar" />
                  <h6 className="bd-sidebar-title">Related Posts</h6>
                  <div className="bd-related-list">
                    {relatedBlogs.map((rel) => (
                      <Link to={`/blog/${rel.id}`} key={rel.id} className="bd-related-item">
                        <div className="bd-related-thumb-wrap">
                          <img
                            src={rel.image}
                            alt={rel.title}
                            className="bd-related-thumb"
                            onError={(e) => { e.target.src = "https://via.placeholder.com/90/243e7c/fff?text=B"; }}
                          />
                        </div>
                        <div className="bd-related-info">
                          <p className="bd-related-title">{rel.title}</p>
                          <span className="bd-related-date">{rel.date}</span>
                        </div>
                        <FiChevronRight className="bd-related-arrow" />
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Categories */}
              <motion.div className="bd-sidebar-card" variants={itemVariants}>
                <div className="bd-sidebar-card-bar" />
                <h6 className="bd-sidebar-title">Categories</h6>
                <div className="bd-categories">
                  {[["Blog Posts", blogsData.length], ["Design Thinking", 1], ["Technology", 2]].map(([label, count]) => (
                    <Link to="/blogs" key={label} className="bd-category-row">
                      <span className="bd-category-dot" />
                      <span className="bd-category-label">{label}</span>
                      <span className="bd-category-badge">{count}</span>
                    </Link>
                  ))}
                </div>
              </motion.div>

            </div>
          </motion.div>

          {/* ── RIGHT: ARTICLE (order-lg-2) ── */}
          <motion.div
            className="col-lg-8 order-lg-2"
            variants={articleVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.article className="bd-article" variants={containerVariants} initial="hidden" animate="visible">

              {/* Excerpt pull quote */}
              <motion.blockquote className="bd-pull-quote" variants={itemVariants}>
                "{blog.excerpt}"
              </motion.blockquote>

              {/* Article body */}
              <motion.div
                className="bd-article-body"
                variants={itemVariants}
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />

              {/* Bottom meta strip */}
              <motion.div className="bd-article-footer" variants={itemVariants}>
                <div className="bd-footer-author-mini">
                  <img
                    src={blog.image}
                    alt={blog.author}
                    className="bd-footer-avatar"
                    onError={(e) => { e.target.src = "https://via.placeholder.com/50/243e7c/fff?text=A"; }}
                  />
                  <div>
                    <p className="bd-footer-name">{blog.author}</p>
                    <p className="bd-footer-role">{blog.role}</p>
                  </div>
                </div>
                <Link to="/blogs" className="bd-back-pill bd-back-pill--outline">
                  <FiArrowLeft />
                  <span>Back to Blogs</span>
                </Link>
              </motion.div>

            </motion.article>
          </motion.div>

        </div>
      </div>
    </div>
  );
}